<template>
  <v-container fluid>
    <v-card class="mb-5">
      <v-card-title>
        <v-icon left>mdi-history</v-icon>
        {{ $t("registroActividad.title") }}
      </v-card-title>
      <v-row>
        <v-col cols="12">
          <registro-actividad-filters
            v-model="filters"
            @updateFilters="setFilters"
            @clearFilter="cleanFilters"
          />
        </v-col>
      </v-row>
    </v-card>

    <v-card>
      <v-col cols="12" class="fill-height">
        <div class="table-fixed-header-wrap">
        <v-data-table
          fixed-header
          height="calc(100vh - 380px)"
          :headers="headers"
          :items="items"
          :server-items-length="totalItems"
          :loading="loading"
          :page.sync="pageNumber"
          :items-per-page.sync="itemsPerPage"
          :sort-by="orderBy"
          :sort-desc="orderDesc"
          :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
          @update:page="fetch"
          @update:items-per-page="fetch"
        >
          <template v-slot:item.user="{ item }">
            {{ item.user ? item.user.username : "-" }}
          </template>

          <template v-slot:item.action="{ item }">
            <v-chip
              :color="getActionColor(item.action)"
              small
              dark
            >
              {{ getActionLabel(item.action) }}
            </v-chip>
          </template>

          <template v-slot:item.esAnomalia="{ item }">
            <div class="text-center">
              <v-tooltip v-if="item.esAnomalia" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    :color="item.nivelAnomalia === 'CRITICAL' ? 'red darken-2' : 'amber darken-2'"
                    :size="22"
                  >
                    mdi-alert-circle
                  </v-icon>
                </template>
                <span>
                  {{ item.motivoAnomalia || $t("registroActividad.destacable.defaultReason") }}
                </span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.createdAt="{ item }">
            {{ getDateTimeFormat(item.createdAt) }}
          </template>

          <template v-slot:item.entity="{ item }">
            {{ getEntityLabel(item.entity) }}
          </template>

          <template v-slot:item.deposito="{ item }">
            {{ item.deposito && item.deposito.nombre ? item.deposito.nombre : "-" }}
          </template>

          <template v-slot:item.details="{ item }">
            <span class="d-block text-wrap" style="min-width: 200px; max-width: 400px; white-space: normal; word-break: break-word;">
              {{ item.details || "-" }}
            </span>
          </template>

          <template v-slot:item.detalle="{ item }">
            <v-btn
              small
              text
              color="primary"
              @click="verDetalle(item)"
            >
              <v-icon small>mdi-eye</v-icon>
              Ver
            </v-btn>
          </template>

          <template v-slot:no-data>
            <div class="text-center" v-t="'common.noData'"></div>
          </template>

          <template v-slot:loading>
            <div class="text-center" v-t="'common.loading'"></div>
          </template>
        </v-data-table>
        </div>
      </v-col>
    </v-card>

    <registro-actividad-detalle
      v-model="mostrarDetalle"
      :registro-id="registroSeleccionadoId"
    />
  </v-container>
</template>

<script>
import RegistroActividadFilters from "./RegistroActividadFilters.vue";
import RegistroActividadDetalle from "./RegistroActividadDetalle.vue";
import RegistroActividadProvider from "../../providers/RegistroActividadProvider";
import { DayjsMixin } from "@dracul/dayjs-frontend";

export default {
  name: "RegistroActividadPage",
  components: { RegistroActividadFilters, RegistroActividadDetalle },
  mixins: [DayjsMixin],
  data() {
    return {
      items: [],
      totalItems: 0,
      loading: false,
      orderBy: "createdAt",
      orderDesc: true,
      itemsPerPage: 25,
      pageNumber: 1,
      search: "",
      filters: [
        { field: "user", operator: "$eq", value: null },
        { field: "action", operator: "$eq", value: null },
        { field: "entity", operator: "$eq", value: null },
        { field: "fechaDesde", operator: "$gte", value: null },
        { field: "fechaHasta", operator: "$lte", value: null },
      ],
      mostrarDetalle: false,
      registroSeleccionadoId: null,
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("registroActividad.labels.fecha"), value: "createdAt", sortable: true },
        { text: this.$t("registroActividad.labels.usuario"), value: "user", sortable: false },
        {
          text: this.$t("registroActividad.labels.destacable"),
          value: "esAnomalia",
          sortable: false,
          align: "center",
        },
        { text: this.$t("registroActividad.labels.accion"), value: "action", sortable: true },
        { text: this.$t("registroActividad.labels.entidad"), value: "entity", sortable: true },
        { text: this.$t("registroActividad.labels.detalles"), value: "details", sortable: false },
        { text: this.$t("registroActividad.labels.deposito"), value: "deposito", sortable: false },
        { text: "", value: "detalle", sortable: false },
      ];
    },
    getOrderBy() {
      return Array.isArray(this.orderBy) ? this.orderBy[0] : this.orderBy;
    },
    getOrderDesc() {
      return Array.isArray(this.orderDesc) ? this.orderDesc[0] : this.orderDesc;
    },
  },
  watch: {
    filters: {
      handler() {
        this.pageNumber = 1;
        this.fetch();
      },
      deep: true,
    },
  },
  created() {
    this.fetch();
  },
  methods: {
    getActionColor(action) {
      const colors = {
        CREATE: "success",
        UPDATE: "info",
        DELETE: "error",
        LOGIN: "primary",
      };
      return colors[action] || "grey";
    },
    getActionLabel(action) {
      return this.$t(`registroActividad.actions.${action}`) || action;
    },
    getEntityLabel(entity) {
      if (!entity) return "-";
      if (entity === "Tamanio") return "Tamaño";
      if (entity === "OrdenVenta") return "Orden de venta";
      if (entity === "OrdenCompra") return "Orden de Compra";
      return entity;
    },
    setFilters(f) {
      this.filters = f;
      this.pageNumber = 1;
      this.fetch();
    },
    cleanFilters() {
      this.filters.forEach((f) => {
        f.value = null;
      });
      this.pageNumber = 1;
      this.fetch();
    },
    verDetalle(item) {
      this.registroSeleccionadoId = item.id;
      this.mostrarDetalle = true;
    },
    async fetch() {
      this.loading = true;
      const fechaDesde = this.filters.find((f) => f.field === "fechaDesde")?.value || null;
      const fechaHasta = this.filters.find((f) => f.field === "fechaHasta")?.value || null;

      const filtersToSend = this.filters
        .filter(
          (f) =>
            f.value != null &&
            f.value !== "" &&
            f.field !== "fechaDesde" &&
            f.field !== "fechaHasta"
        )
        .map((f) => ({ ...f }));

      try {
        const r = await RegistroActividadProvider.paginate(
          this.pageNumber,
          this.itemsPerPage,
          this.search,
          filtersToSend.length ? filtersToSend : null,
          this.getOrderBy,
          this.getOrderDesc,
          fechaDesde,
          fechaHasta
        );
        this.items = r.data.registroActividadPaginate.items;
        this.totalItems = r.data.registroActividadPaginate.totalItems;
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.table-fixed-header-wrap {
  min-height: 0;
}
</style>
